// Purchase requests (purchasing phase 3, docs/plans/purchase-requests.md): staff ask for
// stock, an approver says yes (Approvals, online), and the approved lines become draft
// purchase orders, one per supplier, whose lines point back at them. How much of a request
// is ordered is summed from those order lines, never stored. With purchase_control only.
// Boards ReqList, ReqQuick, ReqNew, ReqNewPhone, ReqDetail, ReqMake.
import {
  buildPurchaseRequest,
  canMovePurchaseRequest,
  purchaseLineCost,
  requestLineCost,
  requestLineLeft,
  setPurchaseRequestStatus,
  type SyncOp,
} from '@ims/core'
import type {
  ProductRow,
  PurchaseRequestBoardRow,
  PurchaseRequestLineRow,
  PurchaseRequestRow,
  RequestLineLeft,
} from '@ims/data'
import {
  Badge,
  Button,
  buttonClass,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconButton,
  IconTile,
  Input,
  Kbd,
  ListFrame,
  ListToolbar,
  Modal,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Segmented,
  Select,
  SkeletonDetail,
  SkeletonTable,
  StateSwap,
  Table,
  TD,
  Textarea,
  TH,
  TR,
  useToast,
} from '@ims/ui'
import {
  ArrowLeft,
  Ban,
  Check,
  ClipboardList,
  FilePen,
  Hand,
  Info,
  Minus,
  Pencil,
  Plus,
  Send,
  Trash2,
  Truck,
} from '@ims/ui/icons'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { DateInput, MoneyInput, ProductPicker, QtyInput, useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { needsApproval, ROLE_WORDS, approvalSteps } from '../lib/approvals.ts'
import { dateLabel, dateShort, money, qty, todayIso } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { numberingFor } from '../lib/numbering.ts'
import { finePointer } from '../lib/pointer.ts'
import {
  groupRequestLines,
  hasLeftToOrder,
  requestDraftPlans,
  requestState,
} from '../lib/purchase-requests.ts'
import { buildReorderDrafts, type DraftPlan } from '../lib/reorder.ts'
import { deleteOp, updateOp } from '../lib/rows.ts'
import { useStaffNames } from '../lib/staff.ts'
import { useUser } from '../session.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { ApprovalCard, requestDoc, useApprovalContext } from './approvals.tsx'
import { SectionTabs } from '../layout/sections.tsx'

const PAGE = 25
const REQUEST_TABLES = [
  'purchase_requests',
  'purchase_request_lines',
  'purchase_orders',
  'purchase_order_lines',
  'locations',
] as const
const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ACTIVE = 'sm:bg-buy-wash sm:shadow-[inset_3px_0_0_var(--color-buy)]'

type Show = 'open' | 'finished'
type Pill = 'toOrder' | 'part' | 'waiting' | 'draft'
const PILLS: { value: Pill; label: string }[] = [
  { value: 'toOrder', label: 'To order' },
  { value: 'part', label: 'Part ordered' },
  { value: 'waiting', label: 'Waiting approval' },
  { value: 'draft', label: 'Draft' },
]

/** Whether a request of this estimate is submitted for approval (else approved at once). */
function useNeedsApproval() {
  const approval = useApprovalContext()
  return (totalPaisa: number) =>
    needsApproval(approval.rules, totalPaisa, approval.featureOn, 'requisition')
}

const requestName = (r: Pick<PurchaseRequestRow, 'number' | 'status'>) =>
  r.number ?? (r.status === 'draft' ? 'Draft' : 'Request (number on sync)')

// -----------------------------------------------------------------------------
// The list
// -----------------------------------------------------------------------------

export function PurchaseRequestsScreen() {
  const settings = useSettings()
  const names = useStaffNames()
  const openItem = useOpen()
  const navigate = useNavigate()
  const canEdit = useCan('staff')
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [active, setActive] = useState<string | null>(null)
  const [making, setMaking] = useState<string[] | null>(null)
  const today = todayIso()
  const show: Show = params.get('show') === 'finished' ? 'finished' : 'open'
  const asked = params.get('status') as Pill | null
  const pill = show === 'open' && PILLS.some((p) => p.value === asked) ? asked : null
  const go = (next: { show?: Show; pill?: Pill | null }) => {
    const s = next.show ?? show
    const p = next.pill === undefined ? (next.show ? null : pill) : next.pill
    setParams(
      { ...(s === 'finished' ? { show: 'finished' } : {}), ...(p ? { status: p } : {}) },
      { replace: true },
    )
    setOffset(0)
    setActive(null)
  }
  const counts = useLive(
    (r) => r.purchases.requestCounts?.(today) ?? Promise.resolve(null),
    [today],
    [...REQUEST_TABLES],
  ).data
  const page = useLive(
    (r) =>
      r.purchases.requestBoard?.({
        show,
        state: pill ?? undefined,
        search,
        today,
        limit: PAGE,
        offset,
      }) ?? Promise.resolve({ rows: [] as PurchaseRequestBoardRow[], total: 0 }),
    [show, pill, search, today, offset],
    [...REQUEST_TABLES],
  ).data
  const rows = page?.rows ?? []
  const openCount = counts ? counts.draft + counts.waiting + counts.toOrder + counts.part : 0
  const ready = counts ? counts.toOrder + counts.part : 0
  const listRef = useRef<HTMLDivElement>(null)
  const activeRow = rows.find((r) => r.request.id === active)
  useListKeys({
    ids: rows.map((r) => r.request.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page?.total ?? 0, setOffset },
    list: listRef,
    open: () => active && navigate(`/purchase-requests/${active}`),
    keys: {
      o: () => activeRow && canEdit && hasLeftToOrder(activeRow) && setMaking([active!]),
    },
  })

  return (
    <div>
      <PageHeader
        title="Purchase orders"
        subtitle={
          counts && openCount > 0 ? (
            <>
              <b className="font-semibold text-ink">{ready} approved to order</b>
              {counts.waiting > 0 && (
                <span className="font-semibold text-warning">
                  {' '}
                  · {counts.waiting} waiting approval
                </span>
              )}
              {counts.late > 0 && (
                <span className="font-semibold text-danger"> · {counts.late} late</span>
              )}
              {counts.draft > 0 && ` · ${counts.draft} draft${counts.draft === 1 ? '' : 's'}`}
            </>
          ) : (
            'Ask for stock. A manager approves it, then it becomes purchase orders.'
          )
        }
        actions={
          canEdit && (
            <>
              {ready > 0 && (
                <Button
                  variant="secondary"
                  className="max-sm:hidden"
                  icon={<ClipboardList className="size-4" />}
                  onClick={() => setMaking([])}
                >
                  Make orders from all approved
                </Button>
              )}
              <Link to="/purchase-requests/new" className={buttonClass()}>
                <ClipboardList className="size-4" aria-hidden />
                New request
              </Link>
            </>
          )
        }
      />
      <SectionTabs section="purchase" />
      <ListToolbar>
        {counts && (
          <Segmented
            label="Open or finished"
            className="max-sm:w-full"
            value={show}
            onChange={(s) => go({ show: s })}
            options={[
              { value: 'open', label: `Open ${openCount}` },
              { value: 'finished', label: `Finished ${counts.finished}` },
            ]}
          />
        )}
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={(v) => {
            setSearch(v)
            setOffset(0)
          }}
          placeholder="Number or item…"
        />
      </ListToolbar>
      {counts && show === 'open' && (
        <div className="mb-3">
          <FilterPills label="Status">
            <FilterPill selected={!pill} count={openCount} onSelect={() => go({ pill: null })}>
              All
            </FilterPill>
            {PILLS.map((p) => (
              <FilterPill
                key={p.value}
                selected={pill === p.value}
                count={counts[p.value]}
                onSelect={() => go({ pill: p.value })}
              >
                {p.label}
              </FilterPill>
            ))}
          </FilterPills>
        </div>
      )}
      <ListFrame ref={listRef}>
        {!page || counts === undefined ? (
          <SkeletonTable cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Hand className="size-6" />}
            title={
              search || pill
                ? 'No requests match'
                : show === 'finished'
                  ? 'No finished requests yet'
                  : 'No open requests'
            }
            description={
              search || pill
                ? 'Try another status or search.'
                : show === 'finished'
                  ? 'Requests that are fully ordered, closed, rejected or cancelled are kept here.'
                  : 'Ask for what the shop needs. Once approved it becomes purchase orders.'
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>Request</TH>
                  <TH>Needed by</TH>
                  <TH>Status</TH>
                  <TH align="right">About</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const r = row.request
                  const state = requestState(row)
                  const to = `/purchase-requests/${r.id}`
                  const late =
                    !!r.needed_by &&
                    r.needed_by < today &&
                    (hasLeftToOrder(row) || r.status === 'submitted')
                  const who = (r.created_by && names[r.created_by]) || 'Someone'
                  return (
                    <TR
                      key={r.id}
                      onClick={() => openItem(to)}
                      aria-current={active === r.id ? 'true' : undefined}
                      className={cx(active === r.id && ROW_ACTIVE)}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            <IconTile area="buy" size="sm">
                              <Hand className="size-4" />
                            </IconTile>
                          }
                          title={
                            <Link
                              to={to}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => {
                                e.stopPropagation()
                                moveClick(to, 'open')(e)
                              }}
                            >
                              {requestName(r)}
                            </Link>
                          }
                          sub={[who, row.locationName].filter(Boolean).join(' · ')}
                        />
                      </TD>
                      <TD className="stack-r3 whitespace-nowrap">
                        <RowNote
                          main={
                            r.needed_by
                              ? dateShort(r.needed_by, settings)
                              : `${row.lineCount} line${row.lineCount === 1 ? '' : 's'}`
                          }
                          sub={
                            r.needed_by
                              ? `${row.lineCount} line${row.lineCount === 1 ? '' : 's'}${late ? ' · late' : ''}`
                              : undefined
                          }
                          tone={late ? 'danger' : undefined}
                          toneOn="sub"
                        />
                      </TD>
                      <TD className="stack-end stack-r2">
                        <StateSwap state={state.label}>
                          <Badge tone={state.tone}>{state.label}</Badge>
                        </StateSwap>
                      </TD>
                      <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                        {money(r.total_paisa, settings)}
                      </TD>
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {canEdit && hasLeftToOrder(row) && (
                            <Button
                              size="sm"
                              variant="soft"
                              className={ROW_BUTTON}
                              icon={<ClipboardList className="size-4" />}
                              aria-label={`Make orders from ${requestName(r)}`}
                              onClick={() => setMaking([r.id])}
                            >
                              Make orders
                            </Button>
                          )}
                          {canEdit && r.status === 'draft' && (
                            <Link
                              to={`/purchase-requests/${r.id}/edit`}
                              aria-label="Edit"
                              title="Edit"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex size-[34px] items-center justify-center rounded-xl border border-border bg-surface text-muted hover:bg-soft"
                            >
                              <Pencil className="size-4" aria-hidden />
                            </Link>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>
                {show === 'open'
                  ? 'To order first, then waiting, then drafts. Amounts are estimates; the order carries the price.'
                  : 'Newest first.'}
              </span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open <Kbd>O</Kbd> make orders
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
      {making && <MakeOrdersDialog requestIds={making} onClose={() => setMaking(null)} />}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Writing a request
// -----------------------------------------------------------------------------

interface EditLine {
  key: string
  /** Null: written in. */
  product: ProductRow | null
  description: string
  qtyMilli: number | null
  costPaisa: number | null
}

/**
 * Writing a request. `edit` changes a draft in place; `copy` starts from a rejected (or any)
 * request and saves a new one.
 */
export function PurchaseRequestEditorScreen({ mode }: { mode: 'new' | 'edit' | 'copy' }) {
  const { id } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const navigate = useNavigate()
  const needs = useNeedsApproval()
  const approval = useApprovalContext()
  const [locationId, setLocationId] = useState<string | null>(null)
  const [neededBy, setNeededBy] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<EditLine[]>([])
  const [busy, setBusy] = useState(false)
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null

  const source = useLive(
    (r) =>
      mode === 'new' || !id ? Promise.resolve(null) : (r.purchases.requestDetail?.(id) ?? null),
    [mode, id],
    ['purchase_requests', 'purchase_request_lines'],
  ).data
  const loaded = useRef<string | null>(null)
  useEffect(() => {
    if (!source || loaded.current === source.request.id) return
    loaded.current = source.request.id
    const stored = source.lines.map((l): EditLine => ({
      key: l.id,
      product: l.product_id ? (source.products[l.product_id] ?? null) : null,
      description: l.description ?? '',
      qtyMilli: l.qty_milli,
      costPaisa: l.est_cost_paisa,
    }))
    // Async on purpose: an effect must not set state while it runs.
    void Promise.resolve().then(() => {
      setLocationId(source.request.location_id)
      setNeededBy(source.request.needed_by ?? '')
      setNotes(source.request.notes ?? '')
      setLines(stored)
    })
  }, [source])

  const add = (product: ProductRow) =>
    setLines((ls) =>
      ls.some((l) => l.product?.id === product.id)
        ? ls
        : [
            ...ls,
            {
              key: crypto.randomUUID(),
              product,
              description: '',
              qtyMilli: product.reorder_qty_milli || null,
              costPaisa: product.cost_price_paisa,
            },
          ],
    )
  const writeIn = () =>
    setLines((ls) => [
      ...ls,
      { key: crypto.randomUUID(), product: null, description: '', qtyMilli: null, costPaisa: 0 },
    ])
  const update = (key: string, patch: Partial<EditLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const total = lines.reduce(
    (sum, l) =>
      sum + requestLineCost({ qtyMilli: l.qtyMilli ?? 0, estCostPaisa: l.costPaisa ?? 0 }),
    0,
  )
  const incomplete =
    lines.length === 0 || lines.some((l) => !l.qtyMilli || (!l.product && !l.description.trim()))
  const submit = needs(total)
  const steps = approvalSteps(approval.rules, total, 'requisition')

  const save = async (asDraft: boolean) => {
    if (!settings || !current || incomplete) return
    setBusy(true)
    try {
      const today = todayIso()
      const stored = mode === 'edit' && id ? await repo.purchases.requestDetail?.(id) : null
      if (mode === 'edit' && stored?.request.status !== 'draft') {
        toast.error('This request has been submitted and can no longer change.')
        return
      }
      const built = buildPurchaseRequest(
        { tenantId: current.tenant_id, now: new Date().toISOString() },
        {
          id: mode === 'edit' ? stored?.request.id : undefined,
          status: asDraft ? 'draft' : submit ? 'submitted' : 'approved',
          locationId: location,
          requestDate: today,
          neededBy: neededBy || null,
          notes,
          lines: lines.map((l) => ({
            productId: l.product?.id ?? null,
            description: l.product ? null : l.description,
            qtyMilli: l.qtyMilli!,
            estCostPaisa: l.costPaisa ?? 0,
          })),
          numbering: asDraft
            ? undefined
            : await numberingFor(repo, settings, 'purchase_request', today),
        },
        stored?.lines ?? [],
      )
      await repo.commit(built.ops)
      toast.success(
        asDraft
          ? 'Draft saved'
          : submit
            ? 'Submitted for approval'
            : 'Request approved: no approval needed at this amount',
      )
      navigate(`/purchase-requests/${built.request.id}`)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const back = mode === 'new' || !id ? '/purchase-requests' : `/purchase-requests/${id}`
  const saveButtons = (
    <>
      <Button
        variant="secondary"
        className="max-sm:flex-1"
        loading={busy}
        disabled={incomplete}
        onClick={() => void save(true)}
      >
        Save draft
      </Button>
      <Button
        icon={<Send className="size-4" />}
        className="max-sm:flex-[1.6]"
        loading={busy}
        disabled={incomplete}
        onClick={() => void save(false)}
      >
        {submit ? 'Submit for approval' : 'Submit'}
      </Button>
    </>
  )
  return (
    <div>
      <Link
        to={back}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />{' '}
        {mode === 'new' ? 'Purchase requests' : 'Back to the request'}
      </Link>
      <PageHeader
        title={
          mode === 'new'
            ? 'New purchase request'
            : mode === 'edit'
              ? 'Edit purchase request'
              : `Copy ${source?.request.number ?? 'purchase request'}`
        }
        subtitle="Ask for stock. A manager approves it, then it becomes purchase orders."
        actions={<span className="flex gap-2 max-sm:hidden">{saveButtons}</span>}
      />
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-4">
            <div className="min-w-0 max-sm:w-full sm:flex-1">
              <ProductPicker
                onPick={add}
                placeholder="Scan or search products to request…"
                autoFocus={finePointer}
              />
            </div>
            <Button variant="ghost" size="sm" icon={<Plus className="size-4" />} onClick={writeIn}>
              Not in the list? Write it in
            </Button>
          </div>
          {lines.length === 0 ? (
            <EmptyState
              icon={<Hand className="size-6" />}
              title="Nothing asked for yet"
              description="Scan a barcode, or type a product name and press Enter. Something you don’t stock yet can be written in."
            />
          ) : (
            <ul className="divide-y divide-border">
              {lines.map((l) => {
                const unit = l.product ? lookups.unit(l.product.unit_id) : null
                const label = l.product?.name ?? (l.description || 'Written-in item')
                return (
                  <li
                    key={l.key}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-4 sm:grid-cols-[minmax(0,1fr)_150px_130px_100px_auto] sm:gap-3"
                  >
                    <div className="col-span-2 min-w-0 sm:col-span-1">
                      {l.product ? (
                        <>
                          <div className="truncate font-medium">{l.product.name}</div>
                          <div className="text-xs text-muted">{l.product.sku}</div>
                        </>
                      ) : (
                        <>
                          <Input
                            aria-label="What you need"
                            placeholder="What you need, e.g. Packing tape 2 inch"
                            maxLength={200}
                            value={l.description}
                            onChange={(e) => update(l.key, { description: e.target.value })}
                          />
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                            <Badge tone="neutral" className="whitespace-nowrap">
                              Not in your products
                            </Badge>
                            matched before it is ordered
                          </div>
                        </>
                      )}
                    </div>
                    <span className="flex items-center justify-self-start overflow-hidden rounded-xl border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 sm:justify-self-end">
                      <button
                        type="button"
                        aria-label={`Less ${label}`}
                        onClick={() =>
                          update(l.key, { qtyMilli: Math.max(1000, (l.qtyMilli ?? 0) - 1000) })
                        }
                        className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink pointer-coarse:size-11"
                      >
                        <Minus className="size-4" />
                      </button>
                      <QtyInput
                        aria-label={`Quantity of ${label}`}
                        milli={l.qtyMilli}
                        allowsDecimal={unit?.allows_decimal ?? false}
                        onValue={(m) => update(l.key, { qtyMilli: m })}
                        className="w-14! min-w-0 rounded-none border-0 px-1! text-center font-semibold tabular focus:ring-0"
                      />
                      <button
                        type="button"
                        aria-label={`More ${label}`}
                        onClick={() => update(l.key, { qtyMilli: (l.qtyMilli ?? 0) + 1000 })}
                        className="flex size-9 shrink-0 items-center justify-center text-muted hover:bg-soft hover:text-ink pointer-coarse:size-11"
                      >
                        <Plus className="size-4" />
                      </button>
                    </span>
                    <MoneyInput
                      aria-label={`About what one ${label} costs`}
                      placeholder="About (each)"
                      paisa={l.costPaisa}
                      onValue={(p) => update(l.key, { costPaisa: p })}
                      className="max-sm:hidden"
                    />
                    <div className="tabular text-right text-sm font-semibold max-sm:hidden">
                      {money(
                        purchaseLineCost({
                          qtyMilli: l.qtyMilli ?? 0,
                          unitCostPaisa: l.costPaisa ?? 0,
                        }),
                        settings,
                      )}
                    </div>
                    <IconButton
                      label={`Remove ${label}`}
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </li>
                )
              })}
              <li className="flex items-baseline justify-between p-4 font-bold">
                <span>About in all</span>
                <span className="tabular">{money(total, settings)}</span>
              </li>
            </ul>
          )}
        </Card>
        <div className="space-y-4">
          <Card className="space-y-4">
            <h3 className="text-[15px] font-semibold">Request</h3>
            {lookups.activeLocations.length > 1 && (
              <Field label="For">
                {(fieldId) => (
                  <Select
                    id={fieldId}
                    value={location ?? ''}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    {lookups.activeLocations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {neededBy && settings ? (
              <DateInput
                label="Needed by"
                value={neededBy}
                onChange={setNeededBy}
                calendar={settings.date_display === 'ad' ? 'ad' : 'bs'}
              />
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setNeededBy(todayIso())}>
                Add a needed-by date
              </Button>
            )}
            <Field label="Why (optional)">
              {(fieldId) => (
                <Textarea
                  id={fieldId}
                  rows={2}
                  maxLength={1000}
                  placeholder="e.g. Dashain week: oil sells faster"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              )}
            </Field>
          </Card>
          <Card>
            <h3 className="text-[15px] font-semibold">Who approves</h3>
            <p className="mt-1 text-sm text-muted">
              {steps.length === 0
                ? `About ${money(total, settings)}: no approval needed; it can be ordered once submitted.`
                : `About ${money(total, settings)}: ${steps
                    .map((s) => `step ${s.level}, ${ROLE_WORDS[s.role]}`)
                    .join('; then ')}.`}{' '}
              Amounts are estimates; the purchase order made from it carries the real price.
            </p>
          </Card>
        </div>
      </div>
      {/* On a phone the buttons wait at the bottom, over the list. */}
      <div className="sticky bottom-24 z-10 mt-4 flex gap-2 rounded-card bg-surface p-2 shadow-card sm:hidden">
        {saveButtons}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// One request
// -----------------------------------------------------------------------------

export function PurchaseRequestDetailScreen() {
  const { id = '' } = useParams()
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const names = useStaffNames()
  const user = useUser()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  const canManage = useCan('manager')
  const toast = useToast()
  const navigate = useNavigate()
  const needs = useNeedsApproval()
  const [busy, setBusy] = useState(false)
  const [ask, setAsk] = useState<null | 'closed' | 'cancelled' | 'delete'>(null)
  const [reason, setReason] = useState('')
  const [making, setMaking] = useState(false)
  const [matching, setMatching] = useState<PurchaseRequestLineRow | null>(null)
  const detail = useLive(
    (r) => r.purchases.requestDetail?.(id) ?? Promise.resolve(null),
    [id],
    [...REQUEST_TABLES, 'products', 'suppliers'],
  )
  if (!detail.data)
    return detail.loading ? <SkeletonDetail /> : <EmptyState title="Purchase request not found" />
  const { request, lines, products, orderedByLine, ordersByLine } = detail.data
  const doneLines = lines.filter((l) => (orderedByLine[l.id] ?? 0) >= l.qty_milli).length
  const startedLines = lines.filter((l) => (orderedByLine[l.id] ?? 0) > 0).length
  const standing = { request, lineCount: lines.length, doneLines, startedLines }
  const state = requestState(standing)
  const left = lines.filter((l) => requestLineLeft(l, orderedByLine) > 0)
  const leftWithProduct = left.filter((l) => l.product_id).length
  const today = todayIso()
  const late =
    !!request.needed_by &&
    request.needed_by < today &&
    (hasLeftToOrder(standing) || request.status === 'submitted')
  const maker = (request.created_by && names[request.created_by]) || 'Someone'
  const mine = !request.created_by || request.created_by === user.id
  const canCancel =
    canEdit &&
    canMovePurchaseRequest(request, 'cancelled') &&
    (mine || canManage) &&
    startedLines === 0
  const canClose = canEdit && canMovePurchaseRequest(request, 'closed') && startedLines > 0
  const unitOf = (l: PurchaseRequestLineRow) =>
    l.product_id ? (lookups.unit(products[l.product_id]?.unit_id)?.symbol ?? '') : ''
  const ctx = () => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })

  const run = async (ops: () => Promise<SyncOp[]> | SyncOp[], success: string) => {
    setBusy(true)
    try {
      await repo.commit(await ops())
      toast.success(success)
      setAsk(null)
      setReason('')
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  const submitDraft = () =>
    run(
      async () => {
        const submit = needs(request.total_paisa)
        return buildPurchaseRequest(
          ctx(),
          {
            id: request.id,
            status: submit ? 'submitted' : 'approved',
            locationId: request.location_id,
            requestDate: todayIso(),
            neededBy: request.needed_by,
            notes: request.notes,
            lines: lines.map((l) => ({
              productId: l.product_id,
              description: l.description,
              qtyMilli: l.qty_milli,
              estCostPaisa: l.est_cost_paisa,
            })),
            numbering: await numberingFor(repo, settings!, 'purchase_request', todayIso()),
          },
          lines,
        ).ops
      },
      needs(request.total_paisa)
        ? 'Submitted for approval'
        : 'Request approved: no approval needed',
    )

  return (
    <div>
      <Link
        to="/purchase-requests"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Purchase requests
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {request.number ??
              (request.status === 'draft'
                ? 'Draft purchase request'
                : 'Request (number on sync)')}{' '}
            <StateSwap state={state.label}>
              <Badge tone={state.tone}>{state.label}</Badge>
            </StateSwap>
          </span>
        }
        subtitle={[
          `Asked by ${maker} on ${dateLabel(request.request_date, settings)}`,
          request.location_id && lookups.activeLocations.length > 1
            ? `for ${lookups.location(request.location_id)?.name ?? 'a location'}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {canEdit && request.status === 'approved' && leftWithProduct > 0 && (
              <Button icon={<ClipboardList className="size-4" />} onClick={() => setMaking(true)}>
                Make orders for {leftWithProduct} line{leftWithProduct === 1 ? '' : 's'}
              </Button>
            )}
            {canEdit && request.status === 'draft' && (
              <>
                <Link
                  to={`/purchase-requests/${request.id}/edit`}
                  className={buttonClass({ variant: 'secondary' })}
                >
                  <Pencil className="size-4" aria-hidden />
                  Edit
                </Link>
                <Button
                  icon={<Send className="size-4" />}
                  loading={busy}
                  onClick={() => void submitDraft()}
                >
                  {needs(request.total_paisa) ? 'Submit for approval' : 'Submit'}
                </Button>
              </>
            )}
            {canEdit && request.status === 'rejected' && (
              <Link
                to={`/purchase-requests/${request.id}/copy`}
                className={buttonClass({ variant: 'secondary' })}
              >
                <FilePen className="size-4" aria-hidden />
                Copy into a new request
              </Link>
            )}
          </>
        }
        more={
          (canClose || canCancel || (canEdit && request.status === 'draft')) && (
            <>
              {canClose && (
                <Button
                  variant="ghost"
                  icon={<Check className="size-4" />}
                  onClick={() => setAsk('closed')}
                >
                  Close the rest
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Ban className="size-4" />}
                  onClick={() => setAsk('cancelled')}
                >
                  Cancel request
                </Button>
              )}
              {canEdit && request.status === 'draft' && (
                <Button
                  variant="ghost"
                  className="text-danger hover:text-danger"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setAsk('delete')}
                >
                  Delete draft
                </Button>
              )}
            </>
          )
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="p-0">
          <Table stack>
            <thead>
              <tr>
                <TH>Item</TH>
                <TH align="right">Asked</TH>
                <TH align="right">About</TH>
                <TH>{request.status === 'approved' || startedLines > 0 ? 'Ordered' : ''}</TH>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const ordered = orderedByLine[l.id] ?? 0
                const refs = ordersByLine[l.id] ?? []
                const product = l.product_id ? products[l.product_id] : null
                const showOrdered = request.status === 'approved' || ordered > 0
                return (
                  <tr key={l.id}>
                    <TD className="stack-main stack-r1">
                      {product ? (
                        <Link
                          to={`/products/${product.id}`}
                          className="font-medium text-ink hover:text-primary hover:underline"
                        >
                          {product.name}
                        </Link>
                      ) : (
                        <div>
                          <span className="font-medium">{l.description}</span>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <Badge tone="neutral">Not in your products</Badge>
                            {canEdit && request.status === 'approved' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-primary"
                                onClick={() => setMatching(l)}
                              >
                                Match to a product
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </TD>
                    <TD align="right" className="stack-start stack-r2 whitespace-nowrap">
                      {qty(l.qty_milli)} {unitOf(l)}
                    </TD>
                    <TD align="right" className="stack-mid stack-r2 text-muted">
                      {money(l.est_cost_paisa, settings)}
                    </TD>
                    <TD className="stack-end stack-r1 sm:w-56">
                      {showOrdered && (
                        <div className="grid gap-1">
                          <Badge
                            tone={
                              ordered >= l.qty_milli ? 'success' : ordered > 0 ? 'info' : 'warning'
                            }
                          >
                            {ordered >= l.qty_milli
                              ? 'On order'
                              : ordered > 0
                                ? `${qty(ordered)} of ${qty(l.qty_milli)} on order`
                                : request.status === 'approved'
                                  ? 'To order'
                                  : 'Not ordered'}
                          </Badge>
                          <span className="block h-1.5 overflow-hidden rounded-full bg-soft">
                            <span
                              className="block h-full bg-buy"
                              style={{ width: `${Math.min(100, (100 * ordered) / l.qty_milli)}%` }}
                            />
                          </span>
                          <span className="text-xs text-muted">
                            {refs.length > 0
                              ? refs.map((ref, i) => (
                                  <span key={ref.orderId}>
                                    {i > 0 && ', '}
                                    <Link
                                      to={`/purchase-orders/${ref.orderId}`}
                                      className="font-semibold text-primary hover:underline"
                                    >
                                      {ref.number ?? 'Draft order'}
                                    </Link>
                                    {ref.supplierName && ` · ${ref.supplierName}`}
                                  </span>
                                ))
                              : !product
                                ? 'Match it to a product to order it'
                                : product.supplier_id
                                  ? 'Goes to its usual supplier'
                                  : 'No usual supplier yet'}
                          </span>
                        </div>
                      )}
                    </TD>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          <div className="flex items-baseline justify-between border-t border-border px-4 py-3 font-bold">
            <span>About in all</span>
            <span className="tabular">{money(request.total_paisa, settings)}</span>
          </div>
        </Card>

        <div className="space-y-4">
          <ApprovalCard doc={requestDoc(request)} />
          <Card>
            <h3 className="mb-1 text-[15px] font-semibold">Details</h3>
            <dl className="text-sm">
              {(
                [
                  ['Asked by', maker],
                  lookups.activeLocations.length > 1 && request.location_id
                    ? ['For', lookups.location(request.location_id)?.name ?? '–']
                    : null,
                  [
                    'Needed by',
                    request.needed_by ? (
                      <span className={cx(late && 'text-danger')}>
                        {dateLabel(request.needed_by, settings)}
                        {late && ' · late'}
                      </span>
                    ) : (
                      '–'
                    ),
                  ],
                  request.notes ? ['Why', request.notes] : null,
                  request.close_reason
                    ? [
                        request.status === 'cancelled' ? 'Cancelled' : 'Closed',
                        request.close_reason,
                      ]
                    : null,
                ] as ([string, ReactNode] | null)[]
              )
                .filter((x): x is [string, ReactNode] => x !== null)
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between gap-3 border-t border-soft py-1.5 first:border-t-0"
                  >
                    <dt className="text-muted">{k}</dt>
                    <dd className="text-right font-semibold">{v}</dd>
                  </div>
                ))}
            </dl>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={ask === 'closed' || ask === 'cancelled'}
        title={ask === 'closed' ? 'Close the rest of this request?' : 'Cancel this request?'}
        confirmLabel={ask === 'closed' ? 'Close the rest' : 'Cancel request'}
        danger={ask === 'cancelled'}
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={() =>
          reason.trim() &&
          void run(
            () =>
              setPurchaseRequestStatus(ctx(), request, ask as 'closed' | 'cancelled', reason).ops,
            ask === 'closed' ? 'The rest is closed' : 'Request cancelled',
          )
        }
      >
        <Field label="Why">
          {(fieldId) => (
            <Textarea
              id={fieldId}
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                ask === 'closed' ? 'e.g. The rest is not needed now' : 'e.g. Asked twice'
              }
            />
          )}
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={ask === 'delete'}
        title="Delete this draft?"
        confirmLabel="Delete draft"
        danger
        busy={busy}
        onClose={() => setAsk(null)}
        onConfirm={async () => {
          if (
            await run(
              () => [
                ...lines.map((l) => deleteOp('purchase_request_lines', l.id)),
                deleteOp('purchase_requests', request.id),
              ],
              'Draft deleted',
            )
          )
            navigate('/purchase-requests')
        }}
      />
      {making && <MakeOrdersDialog requestIds={[request.id]} onClose={() => setMaking(false)} />}
      {matching && (
        <Modal
          open
          onClose={() => setMatching(null)}
          title={`Match “${matching.description}”`}
          size="sm"
        >
          <p className="mb-3 text-sm text-muted">
            Pick the product it is, or add it as a new product first. Then it can be ordered.
          </p>
          <ProductPicker
            autoFocus
            placeholder="Search products…"
            addNew="product"
            onPick={(product) =>
              void run(
                () => [updateOp('purchase_request_lines', matching, { product_id: product.id })],
                `Matched to ${product.name}`,
              ).then((ok) => ok && setMatching(null))
            }
          />
        </Modal>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Making purchase orders from approved requests
// -----------------------------------------------------------------------------

/**
 * The lines still to order of these requests (every approved request when the list is
 * empty), one draft per supplier. Each request line is its own order line, pointing back.
 */
export function MakeOrdersDialog({
  requestIds,
  onClose,
}: {
  requestIds: readonly string[]
  onClose: () => void
}) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const navigate = useNavigate()
  const [chosen, setChosen] = useState<Record<string, string>>({})
  const [skip, setSkip] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const key = requestIds.join(',')
  const data = useLive(
    async (r) => {
      const [left, suppliers] = await Promise.all([
        r.purchases.requestLinesLeft?.(key ? key.split(',') : []) ?? Promise.resolve([]),
        r.list('suppliers', { orderBy: { column: 'name' }, limit: 2000 }),
      ])
      return { left, suppliers: suppliers.rows }
    },
    [key],
    [...REQUEST_TABLES, 'products', 'suppliers'],
  ).data
  const suppliers = data?.suppliers ?? []
  const supplierName = (id: string | null) => suppliers.find((s) => s.id === id)?.name ?? null
  const { groups, unmatched } = groupRequestLines(
    data?.left ?? [],
    chosen,
    new Set(suppliers.map((s) => s.id)),
  )
  // A draft per supplier and location: requests for different places make separate orders.
  const byLocation = new Map<string | null, RequestLineLeft[]>()
  for (const l of data?.left ?? []) {
    const at = l.locationId ?? lookups.activeLocations[0]?.id ?? null
    byLocation.set(at, [...(byLocation.get(at) ?? []), l])
  }
  const plansAt = [...byLocation.entries()].map(([at, ls]) => ({
    at,
    plans: requestDraftPlans(
      groupRequestLines(ls, chosen, new Set(suppliers.map((s) => s.id))).groups,
      skip,
    ),
  }))
  const draftCount = plansAt.reduce((n, p) => n + p.plans.length, 0)
  const numbers = [...new Set((data?.left ?? []).map((l) => l.requestNumber ?? 'a request'))]

  const make = async () => {
    if (!current || draftCount === 0) return
    setBusy(true)
    try {
      const ctx = { tenantId: current.tenant_id, now: new Date().toISOString() }
      const orderIds: string[] = []
      const ops: SyncOp[] = []
      for (const { at, plans } of plansAt) {
        const built = buildReorderDrafts(ctx, plans, { locationId: at, orderDate: todayIso() })
        orderIds.push(...built.orderIds)
        ops.push(...built.ops)
      }
      await repo.commit(ops)
      toast.success(
        orderIds.length === 1 ? 'Draft order made' : `${orderIds.length} draft orders made`,
      )
      onClose()
      navigate(
        orderIds.length === 1 ? `/purchase-orders/${orderIds[0]}` : '/purchase-orders?status=draft',
      )
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const totalOf = (lines: DraftPlan['lines']) => lines.reduce((s, l) => s + purchaseLineCost(l), 0)
  return (
    <Modal
      open
      onClose={onClose}
      title="Make purchase orders"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={draftCount === 0} onClick={() => void make()}>
            {draftCount === 1 ? 'Make the draft order' : `Make ${draftCount} draft orders`}
          </Button>
        </>
      }
    >
      {!data ? (
        <SkeletonTable cols={3} />
      ) : data.left.length === 0 ? (
        <p className="text-sm text-muted">Everything approved is on orders already.</p>
      ) : (
        <div className="grid gap-3 text-sm">
          <p className="text-muted">From {numbers.join(', ')}</p>
          {groups.map((g) => {
            const plan = requestDraftPlans([g], skip)[0]
            return (
              <section
                key={g.supplierId ?? 'none'}
                aria-label={supplierName(g.supplierId) ?? 'No supplier'}
                className="overflow-hidden rounded-xl border border-border"
              >
                <div
                  className={cx(
                    'flex flex-wrap items-center gap-2.5 px-3 py-2',
                    g.supplierId ? 'bg-buy-wash' : 'bg-soft',
                  )}
                >
                  <IconTile area={g.supplierId ? 'buy' : 'neutral'} size="sm">
                    <Truck className="size-4" />
                  </IconTile>
                  <Select
                    dense
                    aria-label={`Supplier for ${g.lines.map((l) => l.product!.name).join(', ')}`}
                    value={g.supplierId ?? ''}
                    onChange={(e) => {
                      const to = e.target.value
                      if (!to) return
                      setChosen((c) => ({
                        ...c,
                        ...Object.fromEntries(g.lines.map((l) => [l.product!.id, to])),
                      }))
                    }}
                    className="min-w-0 flex-1 font-semibold"
                  >
                    {!g.supplierId && <option value="">Choose a supplier</option>}
                    {suppliers
                      .filter((s) => s.is_active || s.id === g.supplierId)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </Select>
                  {plan && <b className="tabular">{money(totalOf(plan.lines), settings)}</b>}
                </div>
                {g.lines.map((l) => {
                  const unit = lookups.unit(l.product!.unit_id)?.symbol ?? ''
                  return (
                    <label
                      key={l.line.id}
                      className="flex cursor-pointer items-center gap-2.5 border-t border-soft px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Order ${l.product!.name} for ${l.requestNumber ?? 'the request'}`}
                        className="size-4 rounded border-border accent-primary"
                        checked={!skip.has(l.line.id)}
                        onChange={(e) =>
                          setSkip((s) => {
                            const next = new Set(s)
                            if (e.target.checked) next.delete(l.line.id)
                            else next.add(l.line.id)
                            return next
                          })
                        }
                      />
                      <span className="min-w-0 flex-1">
                        <b className="block truncate font-semibold">{l.product!.name}</b>
                        <span className="text-xs text-muted">{l.requestNumber ?? 'Request'}</span>
                      </span>
                      <b className="tabular whitespace-nowrap">
                        {qty(l.leftMilli)} {unit}
                      </b>
                    </label>
                  )
                })}
              </section>
            )
          })}
          {unmatched.length > 0 && (
            <p className="flex items-start gap-2 rounded-xl bg-warning-light px-3 py-2.5 text-warning">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <b>{unmatched.map((l) => l.line.description).join(', ')}</b>{' '}
                {unmatched.length === 1 ? 'is' : 'are'} not one of your products. Match{' '}
                {unmatched.length === 1 ? 'it' : 'them'} on the request to put{' '}
                {unmatched.length === 1 ? 'it' : 'them'} on an order.
              </span>
            </p>
          )}
          <p className="text-muted">
            Each goes to its product’s usual supplier unless you choose another. Prices come from
            the product’s cost and can be changed on the draft; nothing is sent.
          </p>
        </div>
      )}
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// "Request more" from a product or a Stock row
// -----------------------------------------------------------------------------

/**
 * Adds a product to this person's draft request (a new one when there is none), or adds it
 * and submits the request. Board ReqQuick.
 */
export function RequestMoreDialog({
  product,
  stockMilli,
  onClose,
}: {
  product: ProductRow
  stockMilli?: number
  onClose: () => void
}) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const user = useUser()
  const { current } = useWorkspace()
  const toast = useToast()
  const needs = useNeedsApproval()
  const unit = lookups.unit(product.unit_id)
  const [amount, setAmount] = useState<number | null>(product.reorder_qty_milli || 1000)
  const [neededBy, setNeededBy] = useState('')
  const [busy, setBusy] = useState(false)
  // This person's newest draft (made here and not synced yet counts as theirs).
  const draft = useLive(
    async (r) => {
      const drafts = await r.list('purchase_requests', {
        where: { status: 'draft' },
        orderBy: { column: 'created_at', direction: 'desc' },
        limit: 50,
      })
      const mine = drafts.rows.find((d) => !d.created_by || d.created_by === user.id)
      return mine ? ((await r.purchases.requestDetail?.(mine.id)) ?? null) : null
    },
    [user.id],
    ['purchase_requests', 'purchase_request_lines'],
  ).data

  const save = async (submitNow: boolean) => {
    if (!current || !settings || !amount) return
    setBusy(true)
    try {
      const stored = draft ?? null
      const lines = (stored?.lines ?? []).map((l) => ({
        productId: l.product_id,
        description: l.description,
        qtyMilli: l.qty_milli + (l.product_id === product.id ? amount : 0),
        estCostPaisa: l.est_cost_paisa,
      }))
      if (!lines.some((l) => l.productId === product.id))
        lines.push({
          productId: product.id,
          description: null,
          qtyMilli: amount,
          estCostPaisa: product.cost_price_paisa ?? 0,
        })
      const total = lines.reduce((s, l) => s + requestLineCost(l), 0)
      const submit = needs(total)
      const today = todayIso()
      const built = buildPurchaseRequest(
        { tenantId: current.tenant_id, now: new Date().toISOString() },
        {
          id: stored?.request.id,
          status: !submitNow ? 'draft' : submit ? 'submitted' : 'approved',
          locationId: stored?.request.location_id ?? lookups.activeLocations[0]?.id ?? null,
          requestDate: today,
          neededBy: (stored?.request.needed_by ?? neededBy) || null,
          notes: stored?.request.notes,
          lines,
          numbering: submitNow
            ? await numberingFor(repo, settings, 'purchase_request', today)
            : undefined,
        },
        stored?.lines ?? [],
      )
      await repo.commit(built.ops)
      toast.success(
        !submitNow
          ? `${product.name} is on your draft request`
          : submit
            ? 'Request submitted for approval'
            : 'Request approved: no approval needed',
      )
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const sym = unit?.symbol ?? ''
  return (
    <Modal
      open
      onClose={onClose}
      title="Request more"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            loading={busy}
            disabled={!amount}
            onClick={() => void save(true)}
          >
            Add and submit
          </Button>
          <Button loading={busy} disabled={!amount} onClick={() => void save(false)}>
            Add to my request
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <div className="flex items-center gap-3 rounded-xl bg-soft p-3">
          <IconTile area="stock" size="sm">
            <Hand className="size-4" />
          </IconTile>
          <span className="min-w-0 flex-1">
            <b className="block truncate">{product.name}</b>
            <span className="text-xs text-muted">
              {stockMilli !== undefined &&
                `${qty(stockMilli)} ${sym} in stock · `.replace('  ', ' ')}
              {product.min_stock_milli > 0 && `alert at ${qty(product.min_stock_milli)} ${sym}`}
            </span>
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="How many"
            hint={product.reorder_qty_milli ? 'Its reorder quantity' : undefined}
          >
            {(fieldId) => (
              <QtyInput
                id={fieldId}
                milli={amount}
                allowsDecimal={unit?.allows_decimal ?? false}
                onValue={setAmount}
              />
            )}
          </Field>
          {!draft && settings && (
            <DateInput
              label="Needed by (optional)"
              value={neededBy || todayIso()}
              onChange={setNeededBy}
              calendar={settings.date_display === 'ad' ? 'ad' : 'bs'}
            />
          )}
        </div>
        <p className="flex items-start gap-2 text-[13px] text-muted">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          {draft
            ? `It goes on your draft request (${draft.lines.length} line${draft.lines.length === 1 ? '' : 's'} so far). Add more, then submit it, or submit it now.`
            : 'It starts a draft request. Add more, then submit it, or submit it now.'}
        </p>
      </div>
    </Modal>
  )
}
